/**
 * Change-password dialog. Also serves the FORCED case: when a user logs in
 * with a temporary password (must_change_password), this opens non-dismissable
 * until they set a real one.
 */
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { api, ApiError } from "../lib/api";
import { useToast } from "../hooks/useToast";
import { useAuth } from "./useAuth";

const MIN_PASSWORD = 8;

export function ChangePasswordDialog({
  open,
  forced,
  onClose,
}: {
  open: boolean;
  forced?: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { refreshUser } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < MIN_PASSWORD;

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setError(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (mismatch || tooShort) return;
    setError(null);
    setBusy(true);
    try {
      await api.changePassword(current, next);
      await refreshUser();
      toast(t("auth.passwordChanged"), "success");
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("auth.genericError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={forced ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown={forced}
    >
      <form onSubmit={submit}>
        <DialogTitle>{t("auth.changePassword")}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {forced && (
            <DialogContentText>{t("auth.mustChangePassword")}</DialogContentText>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t("auth.currentPassword")}
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
            autoFocus
          />
          <TextField
            label={t("auth.newPassword")}
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            required
            error={tooShort}
            helperText={tooShort ? t("auth.passwordTooShort", { min: MIN_PASSWORD }) : " "}
          />
          <TextField
            label={t("auth.confirmPassword")}
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
            error={mismatch}
            helperText={mismatch ? t("auth.passwordMismatch") : " "}
          />
        </DialogContent>
        <DialogActions>
          {!forced && (
            <Button onClick={onClose} color="inherit">
              {t("common.close")}
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            disabled={busy || !current || !next || mismatch || tooShort}
          >
            {t("auth.changePassword")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
